/**
 * BOP Widget Loader
 *
 * Auto-discovers data-bop-widget elements and loads the appropriate widget.
 * Supports both React widgets and vanilla JS widgets.
 *
 * Usage in Webflow (site-wide custom code — one script for everything):
 *   <script type="module" src="https://bop-nyc.web.app/bop-widgets.js"></script>
 *
 * Then on any page, just use data attributes:
 *   <div data-bop-widget="restoration-map" data-project-id="ny-harbor"></div>
 *   <div data-bop-widget="video-showcase"></div>
 *
 * The loader will auto-discover widgets and lazy-load only the JS needed.
 */

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import type { WidgetConfig } from "@bop/shared";

// DOM-enhancement widgets (not React islands) — self-init on load, no-op when
// their target element is absent. Imported for side effects only.
// page-transition runs site-wide: it plays the INBOUND half of the home-hero
// panel navigation (the curtain-lift reveal) on whatever page you land on.
import "./page-transition/page-transition";
import "./accessibility";

// Site-wide card theming (data-card-theme / data-card-art). Injected at
// runtime like the v3-nav styles — appended to <head> so the rules win the
// cascade over Webflow's compiled CSS. An optional inline <style> copy in
// Webflow's head custom code only prevents first-paint flash; this injection
// guarantees the final state and always overrides a stale head copy.
import cardThemesCss from "./card-themes.css?raw";
import { dlog } from "./shared/debug";

(() => {
  const STYLE_ID = "bop-card-themes";
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = cardThemesCss;
  document.head.appendChild(style);

  // Resolve data-card-art masks dynamically so new artwork needs no CSS
  // change: a bare name maps to /card-art/<name>.svg on our hosting, and a
  // full .svg URL (e.g. a Webflow asset) is used as-is. Inline styles also
  // override the static per-name rules in card-themes.css, which remain as
  // documentation/fallback for the original seven.
  const ART_BASE = "https://bop-nyc.web.app/card-art/";
  document.querySelectorAll<HTMLElement>("[data-card-art]").forEach((el) => {
    const name = (el.getAttribute("data-card-art") || "").trim();
    if (!name) return;
    const url = /^https?:\/\/|\.svg(\?|$)/.test(name) ? name : `${ART_BASE}${name}.svg`;
    const v = `url("${url}")`;
    el.style.setProperty("-webkit-mask-image", v);
    el.style.setProperty("mask-image", v);
  });
})();

type WidgetComponent = React.ComponentType<{ config: WidgetConfig }>;

interface WidgetRegistry {
  [widgetType: string]: WidgetComponent;
}

const registry: WidgetRegistry = {};
const mountedRoots = new Map<HTMLElement, Root>();
const loadedScripts = new Set<string>();

// ─── Mapbox GL, on demand ───────────────────────────────
// Mapbox GL is 318 KiB and was loaded by a blocking <script> in Webflow page
// settings, so it downloaded and parsed before first paint on every map page —
// Lighthouse attributed ~7.8s of render blocking to it, the single largest item
// on /restaurants, for a map that sits below the fold.
//
// These widgets need the global `mapboxgl`, so the loader fetches it itself
// right before their bundle mounts (they already only mount near the viewport).
// If a page still has the old blocking tag, `ensureMapbox` sees the global and
// resolves immediately — so this is safe to ship before the tags come out.
const MAPBOX_WIDGETS = new Set<string>([
  "restaurant-map",
  "restoration-map",
  "reef-sites-map",
  "contact-map",
]);

const MAPBOX_VERSION = "v3.9.4";
const MAPBOX_JS = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.js`;
const MAPBOX_CSS = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.css`;

// Kill WebKit's grey/black tap-highlight flash on touch site-wide — interactive
// elements provide their own hover/active feedback (Skye-requested, 2026-08-04).
try {
  const tapStyle = document.createElement("style");
  tapStyle.textContent = "html{-webkit-tap-highlight-color:transparent}";
  document.head.appendChild(tapStyle);
} catch (e) { /* non-DOM environment */ }

let mapboxPromise: Promise<void> | null = null;

/** Load Mapbox GL (JS + CSS) once, shared by every map widget on the page. */
function ensureMapbox(): Promise<void> {
  if (typeof (window as { mapboxgl?: unknown }).mapboxgl !== "undefined") {
    return Promise.resolve();
  }
  if (mapboxPromise) return mapboxPromise;

  mapboxPromise = new Promise<void>((resolve, reject) => {
    // The stylesheet is not render-blocking here (it's injected after paint)
    // but must land before the map draws, or controls/canvas mis-position.
    if (!document.querySelector(`link[href="${MAPBOX_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = MAPBOX_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${MAPBOX_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("mapbox-gl failed")));
      return;
    }

    const script = document.createElement("script");
    script.src = MAPBOX_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later widget retry rather than wedging every map on the page.
      mapboxPromise = null;
      console.error("[BOP] Failed to load Mapbox GL");
      reject(new Error("mapbox-gl failed"));
    };
    document.head.appendChild(script);
  });

  return mapboxPromise;
}

// Lazy-loaded widgets — maps widget type to script path (relative to loader)
// These scripts self-register via __BOP_WIDGETS__.registerWidget() on load.
const VANILLA_WIDGETS: Record<string, string> = {
  "video-showcase": "widgets/edu-video-showcase.js",
  "video-panel": "widgets/video-panel.js",
  "restaurant-map": "widgets/restaurant-map.js",
  "restoration-map": "widgets/restoration-map.js",
  "restoration-overlay": "widgets/restoration-overlay.js",
  "restoration-dashboard": "widgets/restoration-dashboard.js",
  "water-conditions": "widgets/water-conditions.js",
  "trellis-calendar": "widgets/trellis-calendar.js",
  "trellis-checkout": "widgets/trellis-checkout.js",
  "contact-map": "widgets/contact-map.js",
  "reef-sites-map": "widgets/reef-sites-map.js",
  "tide-bar": "widgets/tide-bar.js",
  "carousel": "widgets/carousel.js",
  "add-to-calendar": "widgets/add-to-calendar.js",
  "list-nest": "widgets/list-nest.js",
  "badge-generator": "widgets/badge-generator.js",
  "restoration-steps": "widgets/restoration-steps.js",
  "trillions-experience": "widgets/trillions-experience.js",
  "events-hero-slider": "widgets/events-hero-slider.js",
  "mission-statement": "widgets/mission-statement.js",
  "statement-reveal": "widgets/statement-reveal.js",
  "why-oysters": "widgets/why-oysters.js",
  "faq-accordion": "widgets/faq-accordion.js",
  "report-cite": "widgets/report-cite.js",
  "toast": "widgets/toast.js",
  // Count-only donate (no species/water stat tabs). Same data-bop-widget="donate"
  // attribute + same data-* contract. Swap back to "widgets/donate.js" to restore
  // the full tabbed experience (source preserved at src/donate/).
  "donate": "widgets/donate-count.js",
  // C&P membership form → Webflow tabs rerouting + retry (data-membership-tabs)
  "membership": "widgets/membership.js",
  "v3-nav": "widgets/v3-nav.js",
  "home-hero": "widgets/home-hero.js",
  "report-title": "widgets/report-title.js",
  "lesson-toggle": "widgets/lesson-toggle.js",
  "anchor-spy": "widgets/anchor-spy.js",
  // /innovation#floating-classroom: ticket sign-up, chart pins, pop-up pass
  "floating-classroom": "widgets/floating-classroom.js",
  // /donation/renew + /donation/become-a-tag-holder: personal-link greeting and
  // the prefilled C&P inline form (the widget injects C&P's loader itself)
  "appeal": "widgets/appeal.js",
  "subnav-mega-menu": "subnav-mega-menu.js",
};

// Heavy, below-the-fold widgets (maps, the trillions experience, video showcase,
// events hero). Their scripts pull in large deps — Firebase/Firestore, GSAP,
// model-viewer — so we defer loading them until they're near the viewport,
// keeping them off the initial-load critical path (cuts TBT / main-thread work).
// Above-the-fold instances still load immediately because IntersectionObserver
// fires synchronously for already-visible elements. (perf)
const LAZY_WIDGETS = new Set<string>([
  "trillions-experience",
  "restoration-map",
  "restoration-overlay",
  "reef-sites-map",
  "restaurant-map",
  "contact-map",
  "video-showcase",
  "events-hero-slider",
  "donate",
]);

let lazyIO: IntersectionObserver | null = null;

/** Defer a heavy widget's script load until its element nears the viewport. */
function observeForLazyMount(el: HTMLElement) {
  const type = el.dataset.bopWidget;
  if (!type) return;
  // No IntersectionObserver (very old browser) — just load now.
  if (typeof IntersectionObserver === "undefined") {
    loadVanillaWidget(type);
    return;
  }
  if (!lazyIO) {
    lazyIO = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const target = entry.target as HTMLElement;
          obs.unobserve(target);
          const t = target.dataset.bopWidget;
          if (t) loadVanillaWidget(t);
        }
      },
      { rootMargin: "300px 0px" }
    );
  }
  lazyIO.observe(el);
}

const DEFAULT_API_BASE =
  document.querySelector<HTMLScriptElement>("script[data-bop-api-base]")
    ?.dataset.bopApiBase ?? "";

const selectorObservers = new Map<string, IntersectionObserver>();

/** Load a DOM-enhancement widget only when one of its targets nears view. */
function observeVanillaTarget(type: string, el: HTMLElement) {
  if (loadedScripts.has(VANILLA_WIDGETS[type]!)) return;
  if (typeof IntersectionObserver === "undefined") {
    loadVanillaWidget(type);
    return;
  }
  let observer = selectorObservers.get(type);
  if (!observer) {
    observer = new IntersectionObserver(
      (entries, current) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        current.disconnect();
        selectorObservers.delete(type);
        loadVanillaWidget(type);
      },
      { rootMargin: "100px 0px" }
    );
    selectorObservers.set(type, observer);
  }
  observer.observe(el);
}

/**
 * Register a React component as a widget type.
 */
export function registerWidget(type: string, component: WidgetComponent) {
  registry[type] = component;
  mountWidgetType(type);
}

/**
 * Parse data-* attributes from a mount element into a WidgetConfig.
 */
function parseConfig(el: HTMLElement): WidgetConfig {
  const widgetType = el.dataset.bopWidget ?? "";
  const apiBaseUrl = el.dataset.apiBase ?? DEFAULT_API_BASE;
  const theme = (el.dataset.theme as "light" | "dark") ?? "light";

  const config: WidgetConfig = { widgetType, apiBaseUrl, theme };

  for (const [key, value] of Object.entries(el.dataset)) {
    if (key !== "bopWidget" && key !== "apiBase" && key !== "theme") {
      config[key] = value;
    }
  }

  return config;
}

/**
 * Mount all instances of a specific widget type.
 */
function mountWidgetType(type: string) {
  const Component = registry[type];
  if (!Component) return;

  const elements = document.querySelectorAll<HTMLElement>(
    `[data-bop-widget="${type}"]`
  );

  for (const el of elements) {
    if (mountedRoots.has(el)) continue;

    const config = parseConfig(el);
    el.classList.add("bop-widget", `bop-widget--${type}`);

    try {
      const root = createRoot(el);
      root.render(
        <ErrorBoundary widgetType={type}>
          <Component config={config} />
        </ErrorBoundary>
      );
      mountedRoots.set(el, root);
    } catch (error) {
      console.error(`[BOP] Failed to mount widget "${type}":`, error);
      el.innerHTML = `<div class="bop-widget-error">Unable to load widget</div>`;
    }
  }
}

/**
 * Lazy-load a vanilla JS widget script.
 * The script is self-initializing — it finds its own elements on load.
 */
function loadVanillaWidget(type: string) {
  const scriptPath = VANILLA_WIDGETS[type];
  if (!scriptPath || loadedScripts.has(scriptPath)) return;

  loadedScripts.add(scriptPath);

  // Resolve script URL relative to the loader's own URL
  const loaderScript = document.querySelector<HTMLScriptElement>(
    'script[src*="bop-widgets"]'
  );
  const baseUrl = loaderScript?.src
    ? loaderScript.src.replace(/\/[^/]*$/, "/")
    : "/";

  const inject = () => {
    const script = document.createElement("script");
    script.type = "module";
    script.src = baseUrl + scriptPath;
    script.onerror = () => {
      console.error(`[BOP] Failed to load widget script: ${scriptPath}`);
      loadedScripts.delete(scriptPath);
    };
    document.head.appendChild(script);
  };

  // Map widgets read the global `mapboxgl` on mount and bail silently if it
  // isn't there, so the engine has to land before their bundle runs.
  if (MAPBOX_WIDGETS.has(type)) {
    ensureMapbox().then(inject, () => {
      loadedScripts.delete(scriptPath);
    });
    return;
  }

  inject();
}

/**
 * Scan the page and mount all discovered widgets.
 * React widgets get mounted directly. Vanilla widgets get lazy-loaded.
 */
function mountAll() {
  const elements = document.querySelectorAll<HTMLElement>("[data-bop-widget]");
  dlog(`[BOP Loader] Found ${elements.length} widget(s) on page`);
  for (const el of elements) {
    const type = el.dataset.bopWidget;
    if (!type) continue;

    dlog(`[BOP Loader] Widget type: "${type}"`);
    if (registry[type]) {
      mountWidgetType(type);
    } else if (VANILLA_WIDGETS[type]) {
      if (LAZY_WIDGETS.has(type)) {
        observeForLazyMount(el);
      } else {
        dlog(`[BOP Loader] Lazy-loading vanilla widget: ${VANILLA_WIDGETS[type]}`);
        loadVanillaWidget(type);
      }
    } else {
      console.warn(`[BOP Loader] Unknown widget type: "${type}"`);
    }
  }

  // NOTE: trellis-calendar (data-trellis="calendar") is now owned by the Trellis
  // widgets bundle. The BOP-custom calendar auto-load was removed so the two
  // scripts don't both grab [data-trellis="calendar"] and fight over init.
  // Widget source remains at src/trellis-calendar/ for reference until the
  // Trellis version is confirmed live.

  // Trellis checkout: data-trellis="tt-checkout"
  // Checkout is owned by Trellis' site runtime. Loading the retired BOP
  // checkout here starts a second renderer against the same DOM, mixes ticket
  // data, and requires a series id even when the CMS correctly binds only an
  // occurrence id.

  // Tide bar uses data-bop="tide-day" etc. instead of data-bop-widget
  if (document.querySelector('[data-bop="tide-day"]') && !loadedScripts.has(VANILLA_WIDGETS["tide-bar"]!)) {
    dlog("[BOP Loader] Found tide bar, lazy-loading");
    loadVanillaWidget("tide-bar");
  }

  // Homepage hero choreography is page-specific and relatively substantial;
  // keep it out of the site-wide loader bundle and request it only on home.
  if (document.querySelector(".s-home-hero")) {
    loadVanillaWidget("home-hero");
  }

  // Carousel overflow: data-carousel
  if (document.querySelector('[data-carousel]') && !loadedScripts.has(VANILLA_WIDGETS["carousel"]!)) {
    dlog("[BOP Loader] Found carousel, lazy-loading");
    loadVanillaWidget("carousel");
  }

  // Reusable CMS video panel: data-bop-video-panel
  if (document.querySelector('[data-bop-video-panel]') && !loadedScripts.has(VANILLA_WIDGETS["video-panel"]!)) {
    dlog("[BOP Loader] Found video panel, observing for near-viewport load");
    document.querySelectorAll<HTMLElement>("[data-bop-video-panel]").forEach((el) =>
      observeVanillaTarget("video-panel", el)
    );
  }

  // Membership form rerouting: [data-membership-tabs] (explicit opt-in) OR the
  // Webflow Tabs component authored with the .membership-form-tabs class.
  if (
    document.querySelector('[data-membership-tabs], .membership-form-tabs') &&
    !loadedScripts.has(VANILLA_WIDGETS["membership"]!)
  ) {
    dlog("[BOP Loader] Found membership tabs, lazy-loading");
    loadVanillaWidget("membership");
  }

  // List Nest: data-trellis-nest-source
  if (document.querySelector('[data-trellis-nest-source]') && !loadedScripts.has(VANILLA_WIDGETS["list-nest"]!)) {
    dlog("[BOP Loader] Found list nest, lazy-loading");
    loadVanillaWidget("list-nest");
  }

  // FAQ / generic accordion: [data-accordion] (attribute API) or legacy .c-faq-item.is--accordion
  if (document.querySelector('[data-accordion], .c-faq-item.is--accordion') && !loadedScripts.has(VANILLA_WIDGETS["faq-accordion"]!)) {
    dlog("[BOP Loader] Found FAQ accordion, lazy-loading");
    loadVanillaWidget("faq-accordion");
  }

  // Copy-to-clipboard (name-keyed [data-copy], or legacy .c-report-cite__copy)
  if (document.querySelector('[data-copy], .c-report-cite__copy') && !loadedScripts.has(VANILLA_WIDGETS["report-cite"]!)) {
    dlog("[BOP Loader] Found copy trigger, lazy-loading");
    loadVanillaWidget("report-cite");
  }

  // Generic toast system (any [data-toast] element or [data-toast-show] trigger)
  if (document.querySelector('[data-toast], [data-toast-show]') && !loadedScripts.has(VANILLA_WIDGETS["toast"]!)) {
    dlog("[BOP Loader] Found toast, lazy-loading");
    loadVanillaWidget("toast");
  }

  // Restoration dashboard: loads for the popup section or the inline water-body accordion menu
  if (
    (document.querySelector('.section__resto-dashboard') || document.querySelector('[data-bop-wb-accordion]')) &&
    !loadedScripts.has(VANILLA_WIDGETS["restoration-dashboard"]!)
  ) {
    dlog("[BOP Loader] Found restoration dashboard/accordion, loading");
    loadVanillaWidget("restoration-dashboard");
  }

  // Homepage mission statement H2
  const missionHeading = Array.from(document.querySelectorAll("h2")).find((el) => {
    const text = el.textContent?.replace(/\s+/g, " ").trim().toLowerCase() ?? "";
    return text.includes("billion oyster project is a nonprofit") && text.includes("new york harbor");
  });
  if ((document.querySelector('[data-bop-widget="mission-statement"], [data-bop-mission-media]') || missionHeading) && !loadedScripts.has(VANILLA_WIDGETS["mission-statement"]!)) {
    dlog("[BOP Loader] Found mission statement, lazy-loading");
    loadVanillaWidget("mission-statement");
  }

  // Main navigation subnav / megamenu
  if (document.querySelector('.all-subnavs') && !loadedScripts.has(VANILLA_WIDGETS["subnav-mega-menu"]!)) {
    dlog("[BOP Loader] Found subnav megamenu, lazy-loading");
    loadVanillaWidget("subnav-mega-menu");
  }

  // Format numbers with commas: data-bop="format-number"
  document.querySelectorAll<HTMLElement>('[data-bop="format-number"]').forEach((el) => {
    const num = parseFloat(el.textContent?.replace(/[^0-9.-]/g, "") || "");
    if (!isNaN(num)) el.textContent = num.toLocaleString("en-US");
  });

  // Shorten numbers to K/M: data-bop="format-compact"
  // 70000000 → 70M, 70500000 → 70.5M, 5000 → 5K, 1500 → 1.5K, <1000 → commas.
  document.querySelectorAll<HTMLElement>('[data-bop="format-compact"]').forEach((el) => {
    const num = parseFloat(el.textContent?.replace(/[^0-9.-]/g, "") || "");
    if (isNaN(num)) return;
    const short = (v: number, suffix: string) =>
      `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}${suffix}`;
    if (Math.abs(num) >= 1_000_000) el.textContent = short(num / 1_000_000, "M");
    else if (Math.abs(num) >= 1_000) el.textContent = short(num / 1_000, "K");
    else el.textContent = num.toLocaleString("en-US");
  });
}

/**
 * Error boundary to prevent one broken widget from crashing others.
 */
class ErrorBoundary extends React.Component<
  { widgetType: string; children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { widgetType: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error(`[BOP] Widget "${this.props.widgetType}" crashed:`, error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, color: "#c00", fontFamily: "sans-serif" }}>
          Unable to load widget
        </div>
      );
    }
    return this.props.children;
  }
}

// Expose globally so widget bundles can register themselves
(window as unknown as Record<string, unknown>).__BOP_WIDGETS__ = {
  registerWidget,
  registry,
};

// Auto-mount on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountAll);
} else {
  mountAll();
}

// Watch for dynamically inserted widget elements
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) {
        const type = node.dataset.bopWidget;
        if (type) {
          if (registry[type]) mountWidgetType(type);
          else if (VANILLA_WIDGETS[type]) {
            if (LAZY_WIDGETS.has(type)) observeForLazyMount(node);
            else loadVanillaWidget(type);
          }
        }
        const children = node.querySelectorAll<HTMLElement>("[data-bop-widget]");
        for (const child of children) {
          const childType = child.dataset.bopWidget;
          if (!childType) continue;
          if (registry[childType]) mountWidgetType(childType);
          else if (VANILLA_WIDGETS[childType]) {
            if (LAZY_WIDGETS.has(childType)) observeForLazyMount(child);
            else loadVanillaWidget(childType);
          }
        }

        if (
          (node.matches(".all-subnavs") || node.querySelector(".all-subnavs")) &&
          !loadedScripts.has(VANILLA_WIDGETS["subnav-mega-menu"]!)
        ) {
          loadVanillaWidget("subnav-mega-menu");
        }

        if (
          (node.matches("[data-bop-video-panel]") || node.querySelector("[data-bop-video-panel]")) &&
          !loadedScripts.has(VANILLA_WIDGETS["video-panel"]!)
        ) {
          const targets = node.matches("[data-bop-video-panel]")
            ? [node]
            : Array.from(node.querySelectorAll<HTMLElement>("[data-bop-video-panel]"));
          targets.forEach((target) => observeVanillaTarget("video-panel", target));
        }

        if (
          (node.matches('[data-bop-widget="mission-statement"], [data-bop-mission-media], h2') || node.querySelector('[data-bop-widget="mission-statement"], [data-bop-mission-media], h2')) &&
          !loadedScripts.has(VANILLA_WIDGETS["mission-statement"]!)
        ) {
          const text = node.textContent?.replace(/\s+/g, " ").trim().toLowerCase() ?? "";
          if (node.matches('[data-bop-widget="mission-statement"], [data-bop-mission-media]') || text.includes("billion oyster project is a nonprofit")) {
            loadVanillaWidget("mission-statement");
          }
        }
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });
